import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { documentAction } from 'actions';
import AmatriumTableDocument from 'components/AmatriumTableDocument';
import { Chip, EmptyState, PageContainer, PageHeader, Skeleton } from 'components/ds';
import ConfirmModal from 'components/Modal/ConfirmModal';
import DeleteModal from 'components/Modal/DeleteModal';
import Paginate from 'components/Paginate';
import StateText from 'components/StateText';
import configs from 'configs';
import FilePreviewDialog, { PreviewFileState } from 'containers/Summary/FilePreviewDialog';
import { DEFAULT_BEGIN_PAGE, DEFAULT_SIZE_PAGE, HTTP_CODE_RESPONSE } from 'helpers/constant';
import { ROLES } from 'helpers/enum';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { getUserRolesStorage } from 'helpers/lib';
import { getStorage, STORAGE_KEY } from 'helpers/storage';
import {
  Eye,
  File,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Presentation,
  Search,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FileDocumentState } from 'reducer/document';
import { UploadService } from 'services/upload/upload.service';
import { FileDocument } from 'types/reducers/document';
import { getStorageReadBlobToken } from 'utils/blob';
import './index.css';

// Map document state key -> tone (mockup dot-pill: soft tinted bg + leading dot).
// LOCKED: status TEXT stays as-is (PROCESSED/UNPROCESSABLE/...); only the STYLE changes.
type StateTone = 'success' | 'warning' | 'error' | 'neutral';
const STATE_TONE: Record<string, StateTone> = {
  PROCESSED: 'success',
  SYNCHRONIZED: 'success',
  UPLOADED: 'success',
  SYNCHRONIZING: 'warning',
  PROCESSING: 'warning',
  STOPPED: 'warning',
  FAILED: 'error',
  UNPROCESSABLE: 'error',
  DELETED: 'error',
};
// Soft tinted bg + dot color per tone (DS state tokens).
const TONE_STYLE: Record<StateTone, { bg: string; fg: string; dot: string }> = {
  success: { bg: 'state.successSoft', fg: 'state.success', dot: 'state.success' },
  warning: { bg: 'state.warningSoft', fg: 'state.warning', dot: 'state.warning' },
  error: { bg: 'state.errorSoft', fg: 'state.error', dot: 'state.error' },
  neutral: { bg: 'bg.canvas', fg: 'text.muted', dot: 'text.subtle' },
};

// Mockup unifies the many backend states into 3 user-facing buckets shown in BOTH the filter
// chips and the State column, so the row text always matches the active filter. #77529-3
const STATE_FILTER_BUCKET: Record<string, 'indexed' | 'processing' | 'failed'> = {
  PROCESSED: 'indexed',
  SYNCHRONIZED: 'indexed',
  // UPLOADED = accepted, awaiting AI indexing → "Processing" (matches the server-side
  // status filter: processing = UPLOADED). (#doc-filter)
  UPLOADED: 'processing',
  SYNCHRONIZING: 'processing',
  PROCESSING: 'processing',
  STOPPED: 'processing',
  FAILED: 'failed',
  UNPROCESSABLE: 'failed',
  DELETED: 'failed',
};
const BUCKET_LABEL_KEY: Record<'indexed' | 'processing' | 'failed', string> = {
  indexed: 'document.filterIndexed',
  processing: 'document.filterProcessing',
  failed: 'document.filterFailed',
};
const BUCKET_TONE: Record<'indexed' | 'processing' | 'failed', StateTone> = {
  indexed: 'success',
  processing: 'warning',
  failed: 'error',
};

// Mockup dot-pill: rounded-full soft pill with a leading colored dot + status text.
// Text shows the filter-bucket label (Indexed/Processing/Failed) so it always matches the chips.
const StatePill = ({ stateKey }: { stateKey: string }) => {
  const { t } = useTranslation();
  const bucket = STATE_FILTER_BUCKET[stateKey];
  const tone = bucket ? BUCKET_TONE[bucket] : STATE_TONE[stateKey] ?? 'neutral';
  const s = TONE_STYLE[tone];
  return (
    <Flex
      as='span'
      align='center'
      gap='6px'
      display='inline-flex'
      h='22px'
      px='10px'
      color={s.fg}
      fontSize='xs'
      fontWeight={600}
      lineHeight={1}
      textTransform='capitalize'
      bg={s.bg}
      borderRadius='full'
      whiteSpace='nowrap'
    >
      <Box as='span' flexShrink={0} boxSize='6px' bg={s.dot} borderRadius='full' />
      {bucket ? t(BUCKET_LABEL_KEY[bucket]) : <StateText statusKey={stateKey} />}
    </Flex>
  );
};

// Filter chips -> the real app states they cover (mockup: All / Indexed / Processing / Failed).
// Status filtering is server-side: the chip bucket is forwarded to the list API (?status=)
// so it filters across ALL pages, not just the loaded page. (#doc-filter)
type FilterKey = 'all' | 'indexed' | 'processing' | 'failed';
const FILTER_CHIPS: { key: FilterKey; labelKey: string }[] = [
  { key: 'all', labelKey: 'document.filterAll' },
  { key: 'indexed', labelKey: 'document.filterIndexed' },
  { key: 'processing', labelKey: 'document.filterProcessing' },
  { key: 'failed', labelKey: 'document.filterFailed' },
];

// Pick a lucide file-type icon by filename extension
const getFileTypeIcon = (fileName?: string) => {
  const ext = fileName?.split('.').pop()?.toLowerCase() ?? '';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
  if (['ppt', 'pptx'].includes(ext)) return Presentation;
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return ImageIcon;
  if (['pdf', 'doc', 'docx', 'txt', 'md', 'rtf'].includes(ext)) return FileText;
  return File;
};

const Document = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { data, total_item, loading } = useSelector((state) => state.document);
  const [statusFilter, setStatusFilter] = useState<FilterKey>('all');
  const selectedFiles = useSelector(
    (state) => state.document.data?.filter((file) => file.selected)
  );

  const [currentPage, setCurrentPage] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});
  const [deleteFile, setDeleteFile] = useState<FileDocument>();

  const {
    isOpen: isOpenDeleteDialog,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog,
  } = useDisclosure();

  const {
    isOpen: isOpenDeleteItemDialog,
    onOpen: onOpenDeleteItemDialog,
    onClose: onCloseDeleteItemDialog,
  } = useDisclosure();

  const isSelectedFile = selectedFiles?.length > 0;
  const userRoles = getUserRolesStorage();
  const isAdmin = userRoles?.includes(ROLES.ADMIN) || userRoles?.includes(ROLES.SUPER_ADMIN);
  const isEmpty = !loading && (data?.length ?? 0) === 0;

  // Rows come pre-filtered from the server (the active chip is sent as ?status=), so render
  // the page as-is. (#doc-filter)
  const filteredData = useMemo(() => data ?? [], [data]);

  // NOTE: initial fetch is handled by the debounced search-on-type effect below
  // (fires once on mount with an empty fileName) — keeps a single fetch path.

  const deleteFiles = (files: FileDocumentState[]) => {
    dispatch(documentAction.deleteFiles(files.map((file) => file.id)));
    // Reset to first page after delete
    setCurrentPage(0);
    setSearchValue('');
  };

  const handleChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(event.target.value);

  // Search-on-type: debounce the existing fetch saga (no standalone button).
  // Keeps Redux/pagination wiring identical — just triggers on input change.
  useEffect(() => {
    const handle = setTimeout(() => {
      dispatch(
        documentAction.fetchFiles({
          page: DEFAULT_BEGIN_PAGE,
          fileName: searchValue?.trim(),
          sortField: getStorage(STORAGE_KEY.SORT_FIELD) || 'created_at',
          isAscending: false,
          status: statusFilter === 'all' ? undefined : statusFilter,
        })
      );
      setCurrentPage(0);
    }, 400);
    return () => clearTimeout(handle);
  }, [searchValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRowSelect = (row: FileDocumentState, _rowId: string) => {
    dispatch(
      documentAction.updateSelectFile({
        id: row.id,
      })
    );
  };

  const handleSelectAllFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(documentAction.selectAllFile(e.target.checked));
    },
    [dispatch]
  );

  const handleView = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: FileDocument) => {
      e.stopPropagation();
      onOpenFile(item);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, item: FileDocument) => {
      e.stopPropagation();
      setDeleteFile(item);
      onOpenDeleteItemDialog();
    },
    [onOpenDeleteItemDialog]
  );

  const onDeleteItem = () => {
    if (deleteFile) {
      deleteFiles([deleteFile]);
    }
  };

  const tableColumns = useMemo(() => {
    return [
      ...(isAdmin
        ? [
            {
              Header: 'Select',
              accessor: 'selected',
              key: 'selected',
              Filter: '',
              filter: '',
              disableSortBy: true,
              type: 'checkbox',
              width: '60px',
              titleClickHandler: handleSelectAllFile,
            },
          ]
        : []),
      {
        Header: t('document.name'),
        accessor: 'name',
        key: 'name',
        Filter: '',
        filter: '',
        // NAME flexes to fill remaining space, then ellipsis-truncates.
        width: 'auto',
        minWidth: '200px',
        truncate: true,
        render: (info: any) => {
          // Don't render an icon/row content for an empty/blank row (e.g. search no-result). #77529r2-2
          if (!info?.name) return null;
          // Bare file-type icon (no gray tile) + truncating filename (mockup)
          const FileIcon = getFileTypeIcon(info?.name);
          return (
            <Flex align='center' gap={3} minW={0}>
              <Flex align='center' justify='center' flexShrink={0} color='text.muted'>
                <FileIcon size={18} />
              </Flex>
              <Tooltip p={2} whiteSpace={'pre-wrap'} label={info.name}>
                <Text overflow={'hidden'} whiteSpace={'nowrap'} textOverflow={'ellipsis'}>
                  {info.name}
                </Text>
              </Tooltip>
            </Flex>
          );
        },
      },
      {
        Header: t('document.source'),
        accessor: 'source',
        key: 'source',
        Filter: '',
        filter: '',
        // Compact — short token, never needs full width.
        width: '120px',
        disableSortBy: true,
        render: (info: any) => {
          return <>{info?.source ? <StateText statusKey={info.source} /> : null}</>;
        },
      },
      {
        Header: t('document.state'),
        accessor: 'state',
        key: 'state',
        Filter: '',
        filter: '',
        width: '130px',
        render: (info: any) => {
          if (!info?.state) return null;
          // Mockup dot-pill: soft tinted rounded-full pill + leading colored dot.
          return <StatePill stateKey={info.state} />;
        },
      },
      {
        Header: t('document.uploadedBy'),
        accessor: 'uploadedBy',
        key: 'uploadedBy',
        Filter: '',
        filter: '',
        // Cap + ellipsis-truncate so long emails never widen the table.
        maxWidth: '180px',
        truncate: true,
        disableSortBy: true,
        render: (info: any) => {
          return (
            <Tooltip p={2} label={info.uploadedBy}>
              <Text overflow='hidden' whiteSpace='nowrap' textOverflow='ellipsis'>
                {info?.uploadedBy ? <>{info.uploadedBy}</> : null}
              </Text>
            </Tooltip>
          );
        },
      },
      {
        Header: t('document.uploadedAt'),
        accessor: 'created_date',
        key: 'created_date',
        Filter: '',
        filter: '',
        // Compact date column.
        width: '150px',
      },
      {
        Header: t('document.size'),
        accessor: 'size',
        key: 'size',
        Filter: '',
        filter: '',
        // SIZE — compact, mono, LEFT-aligned tabular numbers (#77529r2-1).
        width: '90px',
        render: (info: any) => {
          return info.size ? (
            <Text fontFamily='mono' textAlign='left' style={{ fontVariantNumeric: 'tabular-nums' }}>
              {info.size.toFixed(2)}
            </Text>
          ) : null;
        },
      },
      {
        Header: t('document.actions'),
        accessor: 'action',
        key: 'action',
        Filter: '',
        filter: '',
        // ACTIONS — compact eye + trash.
        width: '90px',
        disableSortBy: true,
        actionItems: [
          ...(isAdmin
            ? [
                {
                  key: 'view',
                  'aria-label': 'View file',
                  label: t('common.view'),
                  icon: <Eye size={18} />,
                  onClick: handleView,
                  color: 'primary.solid',
                },
                {
                  key: t('common.delete'),
                  'aria-label': 'Delete file',
                  label: t('common.delete'),
                  icon: <Trash2 size={18} />,
                  onClick: handleDelete,
                  color: 'state.error',
                },
              ]
            : [
                {
                  key: 'view',
                  'aria-label': 'View file',
                  label: t('common.view'),
                  icon: <Eye size={18} />,
                  onClick: handleView,
                  color: 'primary.solid',
                  textAlign: 'center',
                },
              ]),
        ],
      },
    ];
  }, [t, isAdmin, handleSelectAllFile, handleView, handleDelete]);

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: FileDocument) => {
    readFile(file);
  };

  const readFile = (file: FileDocument) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: FileDocument) => {
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  return (
    <>
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />
      <Stack>
        <DeleteModal
          isOpen={isOpenDeleteDialog}
          onClose={onCloseDeleteDialog}
          recordDelete={selectedFiles}
          action={deleteFiles}
          dataHeader={t('document.deleteFile')}
          dataBody={t('document.deleteFileInstruction')}
          btnAction={t('common.delete')}
          color={'red'}
        />
        <ConfirmModal
          isOpen={isOpenDeleteItemDialog}
          onClose={onCloseDeleteItemDialog}
          action={onDeleteItem}
          dataHeader={`${t('document.deleteFile')} ${deleteFile?.name}`}
          dataBody={t('document.deleteFileInstruction')}
          btnAction={t('common.delete')}
          color={'red'}
        />
        <PageContainer>
          {/* Page header — LEFT-aligned title + count subtitle. Upload action removed (#77529r2-1;
              uploading is reachable from the topbar/Knowledge-base nav). */}
          <PageHeader
            title={t('document.documents')}
            subtitle={t('document.subtitle_count', { count: total_item ?? 0 })}
          />

          <Stack spacing={4}>
            {/* Toolbar — search-icon input + search/delete DS buttons (mockup .toolbar) */}
            <HStack flexWrap='wrap' spacing={3}>
              <InputGroup w={{ base: '100%', md: '50%' }} maxW='360px'>
                <InputLeftElement h='40px' color='text.subtle' pointerEvents='none'>
                  <Search size={16} />
                </InputLeftElement>
                <Input
                  h='40px'
                  pl='38px'
                  color='text.default'
                  bg='bg.surface'
                  borderWidth='1px'
                  borderColor='border.strong'
                  borderRadius='md'
                  _hover={{ borderColor: 'border.strong' }}
                  _focus={{
                    borderColor: 'primary.solid',
                    boxShadow: '0 0 0 3px var(--chakra-colors-primary-soft)',
                  }}
                  _placeholder={{ color: 'text.subtle' }}
                  aria-label={t('document.searchByFileName')}
                  onChange={handleChangeSearchValue}
                  placeholder={t('document.searchByFileName')}
                  value={searchValue}
                />
              </InputGroup>
              {isSelectedFile && isAdmin && (
                <Button
                  leftIcon={<Trash2 size={16} />}
                  onClick={onOpenDeleteDialog}
                  size='ds-md'
                  variant='ds-danger-soft'
                >
                  {t('common.delete')}
                </Button>
              )}
            </HStack>

            {/* Status filter chips — All / Indexed / Processing / Failed (mockup) */}
            <HStack flexWrap='wrap' spacing={2}>
              {FILTER_CHIPS.map((chip) => (
                <Chip
                  key={chip.key}
                  active={statusFilter === chip.key}
                  onClick={() => {
                    setStatusFilter(chip.key);
                    setCurrentPage(0);
                    dispatch(
                      documentAction.fetchFiles({
                        page: DEFAULT_BEGIN_PAGE,
                        fileName: searchValue?.trim(),
                        sortField: getStorage(STORAGE_KEY.SORT_FIELD) || 'created_at',
                        isAscending: getStorage(STORAGE_KEY.IS_ASCENDING) || false,
                        status: chip.key === 'all' ? undefined : chip.key,
                      })
                    );
                  }}
                >
                  {t(chip.labelKey)}
                </Chip>
              ))}
            </HStack>

            {/* Table wrapper — mockup .table-wrap (r-lg border surface) */}
            <Box
              overflow='hidden'
              bg='bg.surface'
              borderWidth='1px'
              borderColor='border.default'
              borderRadius='lg'
            >
              <Box overflowX={'auto'}>
                {loading ? (
                  /* DS loading skeleton — mirrors table rows (design-guidelines.md §4) */
                  <Box p={4}>
                    <Skeleton h='14px' w='40%' mb={4} />
                    {Array.from({ length: 8 }).map((_, i) => (
                      <Skeleton key={i} h='44px' borderRadius='md' mb={2} />
                    ))}
                  </Box>
                ) : isEmpty || filteredData.length === 0 ? (
                  /* DS empty state (design-guidelines.md §4) — also when a chip
                     filters every loaded row out. */
                  <EmptyState icon={<FileText size={26} />} title={t('document.noData')} />
                ) : (
                  <AmatriumTableDocument
                    columns={tableColumns}
                    data={filteredData}
                    isLoading={loading}
                    documentName={searchValue}
                    onRowSelect={onRowSelect}
                  />
                )}
              </Box>
              <Box bg='transparent' borderColor='border.default' borderTopWidth='1px'>
                <Paginate
                  // Server returns the filtered total, so page off total_item directly. (#doc-filter)
                  total={total_item}
                  current={currentPage}
                  pageSize={DEFAULT_SIZE_PAGE}
                  onChange={(page: number) => {
                    setCurrentPage(page);
                    dispatch(
                      documentAction.fetchFiles({
                        page,
                        fileName: searchValue,
                        sortField: getStorage(STORAGE_KEY.SORT_FIELD) || 'created_at',
                        isAscending: getStorage(STORAGE_KEY.IS_ASCENDING) || false,
                        status: statusFilter === 'all' ? undefined : statusFilter,
                      })
                    );
                  }}
                  disabled={loading}
                />
              </Box>
            </Box>
          </Stack>
        </PageContainer>
      </Stack>
    </>
  );
};

export default Document;
